import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import TextInput from "components/ui/TextInput";
import { kakaoAuthorizeUrl } from "config/kakao";
import { naverAuthorizeUrl, naverLoginVisible } from "config/naver";
import { appleAuthorizeUrl, appleWebLoginEnabled } from "config/apple";
import { appleLogin as nativeAppleLogin, openSignupTerms } from "common/nativeBridge";
import { detectRuntime, Runtime } from "util/userAgent";
import { openNativeOrWeb } from "common/nativeRoute";
import { setConsentMode } from "util/consent";
import { getLastLoginProvider, LOGIN_PROVIDER } from "common/lastLoginProvider";
import kakaoIcon from "assets/icons/ic-kakao.svg";
import naverIcon from "assets/icons/ic-naver.svg";
// ★ 2026-11-04 애플 로고는 그림 파일로 — 글자 \uF8FF(애플 로고)는 애플 기기 글꼴에만 있어 AOS·윈도우에선 네모로 깨졌다.
import appleIcon from "assets/icons/ic-apple.svg";

// 아이디·비밀번호 저장(자동채움) — localStorage(유니코드 안전 base64로 약간 가림).
const REMEMBER_KEY = "muyeon_login_remember";
const encode = (obj) => btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
const decode = (raw) => JSON.parse(decodeURIComponent(escape(atob(raw))));

/**
 * 무용연 로그인 — 후니드 로그인 DOM 구조 그대로 사용.
 * 패딩은 `#login .content-box{padding:0 3.2rem}` 에서 옴 (.content-wrap 아님).
 */
const Login = () => {
  const navigate = useNavigate();
  const { login, appleLogin } = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/home";

  // 애플 로그인 버튼은 iOS 앱(웹뷰)에서만 노출 — 애플 OAuth 는 웹뷰 외 환경에서만 가능하고,
  // 시트는 네이티브 ASAuthorization 으로 띄움. 브라우저/안드로이드에선 숨김.
  const isIOSApp = detectRuntime() === Runtime.IOS_WEBVIEW;
  // ★ 11-04 iOS 앱은 네이티브 시트, 그 밖(브라우저·AOS 앱)은 애플 웹 로그인(Services ID 가 설정됐을 때만).
  const showApple = isIOSApp || appleWebLoginEnabled();
  const showNaver = naverLoginVisible(); // 네이버 검수 전에는 ?naver=1 로 들어온 탭에서만

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [msg, setMsg] = useState("");
  // 애플 로그인 진행 중 — 버튼 잠금용.
  //  ⚠️ 네이티브 시트는 사용자가 Face ID 까지 하는 흐름이라 길면 수십 초다(브릿지 제한시간 3분).
  //     그동안 버튼이 열려 있으면 연타로 요청이 브릿지 대기큐(waitQueueByType)에 쌓이고,
  //     콜백은 FIFO 로 매칭돼 응답이 엇갈릴 수 있다.
  const [appleBusy, setAppleBusy] = useState(false);
  //  ⚠️ state 는 갱신이 비동기라 같은 틱에 두 번 탭하면 둘 다 통과한다. 실제 차단은 ref 가 한다.
  //     state 는 버튼 비활성·문구 표시용이다.
  const appleLock = useRef(false);
  // [회원가입] → 가입 방법 선택(카카오·Apple·이메일). 소셜은 처음 쓰는 계정이면 그 자리에서 가입된다.
  //  ⚠️ Apple 4.8 — 카카오를 보여주는 곳엔 Apple 도 같은 위치·크기로 둔다(iOS 앱).
  const [signupOpen, setSignupOpen] = useState(false);
  const [lastLoginProvider] = useState(getLastLoginProvider);

  // 로그인 화면에만 표시한다. 가입 방법 선택창에서는 가입 수단으로 오해하지 않게 숨긴다.
  const recentLoginHint = (provider, style) => lastLoginProvider === provider && (
    <div className="tag-group flex-box-ct" style={{ width: "100%", marginBottom: "0.4rem", ...style }}>
      <span className="level very-row">최근 로그인</span>
    </div>
  );

  // 저장된 아이디/비밀번호 자동 채움
  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const d = decode(raw);
        if (d?.email) setEmail(d.email);
        if (d?.password) setPassword(d.password);
        setRemember(true);
      }
    } catch {}
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) {
      setMsg("이메일과 비밀번호를 입력해 주세요.");
      return;
    }
    try {
      // 아이폰 키보드 자동완성이 이메일 끝에 공백을 붙이면 '비밀번호가 올바르지 않습니다'로 실패했다(2026-10-04).
      await login(email.trim(), password);
      // 로그인 성공 시 저장/삭제
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, encode({ email, password }));
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {}
      navigate(decodeURIComponent(redirect), { replace: true });
    } catch (err) {
      setMsg(err?.response?.data?.message || "로그인에 실패했어요.");
    }
  };

  // 회원가입 진입 전 약관 동의 먼저. 앱이면 네이티브 동의화면, 웹이면 /signupTerms.
  const goRegister = () => {
    setSignupOpen(false);
    setConsentMode("signup");
    openNativeOrWeb(openSignupTerms, { mode: "signup", redirect }, navigate);
  };

  // 카카오 로그인: 카카오 인증 페이지로 이동. redirect 를 state 로 실어 로그인 후 복원.
  const goKakao = () => {
    window.location.href = kakaoAuthorizeUrl(redirect);
  };

  // ★ 2026-10-31 네이버 로그인: 네이버 인증 페이지로 이동(state 에 돌아갈 경로를 담는다 — config/naver).
  const goNaver = () => {
    window.location.href = naverAuthorizeUrl(redirect);
  };

  // 애플 로그인: 네이티브 시트 → identityToken 수신 → 백엔드 검증 → 로그인.
  const goApple = async () => {
    if (!isIOSApp) { window.location.href = appleAuthorizeUrl(redirect); return; }
    if (appleLock.current) return;
    appleLock.current = true;
    setMsg("");
    setAppleBusy(true);
    try {
      const res = await nativeAppleLogin(); // { status, data:[{identityToken, name?}] }
      const cred = res?.status === 200 ? (Array.isArray(res.data) ? res.data[0] : res.data) : null;
      if (!cred?.identityToken) {
        setMsg("애플 로그인이 취소됐어요.");
        return;
      }
      const result = await appleLogin(cred);
      if (result?.isNew) {
        navigate(`/social-signup?redirect=${encodeURIComponent(redirect)}`, { replace: true });
      } else {
        navigate(decodeURIComponent(redirect), { replace: true });
      }
    } catch (err) {
      // 네이티브 시트가 제한시간을 넘긴 경우와 서버 검증 실패를 구분한다.
      //  둘 다 "실패했습니다" 로 뭉뚱그리면 사용자는 뭘 다시 해야 할지 알 수 없다.
      if (String(err?.message || "").includes("Native timeout")) {
        setMsg("응답이 지연됐어요. 다시 시도해 주세요.");
        return;
      }
      setMsg(err?.response?.data?.message || "애플 로그인에 실패했어요.");
    } finally {
      // 성공 시에는 화면을 떠나므로 의미가 없지만, 취소·실패로 남는 경우 반드시 풀어야 한다.
      appleLock.current = false;
      setAppleBusy(false);
    }
  };

  // 로그인 화면 — 참고 UI처럼 원형 아이콘으로 표시한다.
  const socialLoginButtons = (
    <div className="flex-box-ct" style={{ gap: "2.4rem", width: "100%" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ minHeight: "2rem", marginBottom: "0.6rem" }}>
          {lastLoginProvider === LOGIN_PROVIDER.KAKAO && (
            <div className="tag-group"><span className="level very-row">최근 로그인</span></div>
          )}
        </div>
        <button
          type="button"
          aria-label="카카오로 시작하기"
          onClick={goKakao}
          style={{ width: "6.4rem", height: "6.4rem", borderRadius: "50%", overflow: "hidden", flex: "0 0 auto" }}
        >
          <img src={kakaoIcon} alt="" style={{ display: "block", width: "100%", height: "100%" }} />
        </button>
      </div>

      {showNaver && (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ minHeight: "2rem", marginBottom: "0.6rem" }}>
          {lastLoginProvider === LOGIN_PROVIDER.NAVER && (
            <div className="tag-group"><span className="level very-row">최근 로그인</span></div>
          )}
        </div>
        <button
          type="button"
          aria-label="네이버로 시작하기"
          onClick={goNaver}
          style={{ width: "6.4rem", height: "6.4rem", borderRadius: "50%", overflow: "hidden", flex: "0 0 auto" }}
        >
          <img src={naverIcon} alt="" style={{ display: "block", width: "100%", height: "100%" }} />
        </button>
      </div>
      )}

      {showApple && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ minHeight: "2rem", marginBottom: "0.6rem" }}>
            {lastLoginProvider === LOGIN_PROVIDER.APPLE && (
              <div className="tag-group"><span className="level very-row">최근 로그인</span></div>
            )}
          </div>
          <button
            type="button"
            aria-label="Apple로 계속하기"
            onClick={goApple}
            disabled={appleBusy}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: "6.4rem", height: "6.4rem", borderRadius: "50%", flex: "0 0 auto",
              background: "#000", color: "#fff", opacity: appleBusy ? 0.6 : 1,
            }}
          >
            <img src={appleIcon} alt="" aria-hidden="true" style={{ display: "block", width: "3rem", height: "3rem", marginTop: "-0.2rem" }} />
          </button>
        </div>
      )}
    </div>
  );

  // 회원가입 방법 선택창 — 기존 전체 너비 버튼을 유지한다.
  const signupSocialButtons = (
    <>
      {/* 카카오 로그인 (OAuth 리다이렉트) */}
      <button
        type="button"
        className="btn-md"
        onClick={goKakao}
        style={{ width: "100%", background: "#FEE500", color: "#191600", border: "none" }}
      >
        <i className="kakao" style={{ marginRight: "0.6rem" }} />
        카카오로 시작하기
      </button>

      {/* ★ 2026-10-31 네이버 로그인 (OAuth 리다이렉트) — 네이버 로그인 버튼 규정 색 #03C75A */}
      {showNaver && (
      <button
        type="button"
        className="btn-md"
        onClick={goNaver}
        style={{ width: "100%", background: "#03C75A", color: "#fff", border: "none", marginTop: "0.8rem" }}
      >
        <img src={naverIcon} alt="" style={{ width: "2rem", height: "2rem", marginRight: "0.6rem", verticalAlign: "middle" }} />
        네이버로 시작하기
      </button>
      )}

      {/* 애플 로그인 — iOS 앱은 네이티브 시트, 그 밖은 애플 웹 로그인(config/apple) */}
      {showApple && (
        <button
          type="button"
          className="btn-md"
          onClick={goApple}
          disabled={appleBusy}
          style={{
            width: "100%", background: "#000", color: "#fff", border: "none",
            marginTop: "0.8rem", opacity: appleBusy ? 0.6 : 1,
          }}
        >
          <img src={appleIcon} alt="" aria-hidden="true" style={{ width: "1.8rem", height: "1.8rem", marginRight: "0.6rem", verticalAlign: "middle", marginTop: "-0.3rem" }} />
          {appleBusy ? "진행 중..." : "Apple로 계속하기"}
        </button>
      )}
    </>
  );

  return (
    <div className="container">
      <div className="top-bar">
        <button type="button" className="ico-btn back" onClick={() => navigate("/home")}>
          <i className="arrow" />
          <span className="visually-hidden">뒤로가기</span>
        </button>
        <h2 className="page-name">로그인</h2>
      </div>

      <div id="login" className="page">
        <div className="content-box">
          <p className="greetings t-22-multi fw-b fc-h">
            무용연에<br />오신 것을 환영해요
          </p>

          <form className="form-wrap" onSubmit={submit}>
            <div className="form-box">
              <TextInput label="이메일" name="email" type="email" required isValid
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="이메일" maxLength={50} />
              <TextInput label="비밀번호" name="password" type="password" required isValid
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="비밀번호" maxLength={50} />
            </div>

            <label className="flex-box-st-ct" style={{ gap: "0.6rem", margin: "0.8rem 0 0.4rem", cursor: "pointer" }}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span className="t-14-single">아이디·비밀번호 저장</span>
            </label>

            {msg && (
              <p className="t-14-single" style={{ color: "#e2362d" }}>{msg}</p>
            )}

            {recentLoginHint(LOGIN_PROVIDER.EMAIL)}
            <div className="button-group">
              <button type="submit" className="btn-md filled primary">로그인</button>
              <button type="button" className="btn-md outlined" onClick={() => setSignupOpen(true)}>회원가입</button>
            </div>

            {/* 소셜 로그인 구분선 */}
            <div className="flex-box-ct" style={{ gap: "0.8rem", margin: "1.6rem 0 1.2rem" }}>
              <span style={{ flex: 1, height: 1, background: "#e5e5e5" }} />
              <span className="t-13-single">또는</span>
              <span style={{ flex: 1, height: 1, background: "#e5e5e5" }} />
            </div>

            {socialLoginButtons}

            <div className="other-pages flex-box-ct">
              <button type="button" className="t-btn" onClick={() => navigate("/accountRecovery?type=id")}>아이디 찾기</button>
              <span style={{ color: "#d0d0d0" }}>|</span>
              <button type="button" className="t-btn" onClick={() => navigate("/accountRecovery?type=pw")}>비밀번호 찾기</button>
            </div>
          </form>
        </div>
      </div>

      {/* 가입 방법 선택 — 공통 모달 구조(modal-dimmed/modal) 재사용 */}
      <div data-id="modal" className={`modal-dimmed ${signupOpen ? "is-show" : ""}`} onClick={() => setSignupOpen(false)}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="tit-box">
            <p>무용연 시작하기</p>
          </div>
          <div className="con-box">
            {signupSocialButtons}
            <button type="button" className="btn-md outlined" onClick={goRegister} style={{ width: "100%", marginTop: "0.8rem" }}>
              이메일로 가입하기
            </button>
            {msg && (
              <p className="t-14-single" style={{ color: "#e2362d", marginTop: "0.8rem" }}>{msg}</p>
            )}
          </div>
          <div className="btn-group">
            <button type="button" className="btn-md outlined" onClick={() => setSignupOpen(false)}>닫기</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
